import React, { useState, useEffect } from 'react';
import { Table, Button, Input, Modal, Form, Spin, Select, Row, Col, Tabs } from 'antd';
import { FaRegTrashAlt, FaEdit, FaUndo } from 'react-icons/fa';
import { PlusOutlined } from '@ant-design/icons';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Api from '../api/Api';

const { Option } = Select;
const { TabPane } = Tabs;

const Fornecedor = () => {
  const [fornecedoresAtivos, setFornecedoresAtivos] = useState([]);
  const [fornecedoresInativos, setFornecedoresInativos] = useState([]);
  const [nacionalidades, setNacionalidades] = useState([]);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [selectedFornecedor, setSelectedFornecedor] = useState(null);
  const [fornecedorToDelete, setFornecedorToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);

  useEffect(() => {
    document.title = 'Fornecedores';
    fetchFornecedoresAtivos();
    fetchFornecedoresInativos();
    fetchNacionalidades();
  }, []);

  const sms_error = (msg) => toast.error(msg);
  const sms_success = (msg) => toast.success(msg);

  const fetchFornecedoresAtivos = async () => {
    setLoading(true);
    try {
      const response = await Api.ListFornecedor(); // espera os activos
      const dados = Array.isArray(response) ? response : [];
      setFornecedoresAtivos(dados.map(item => ({
        key: item.id,
        nome: item.nome,
        nif: item.nif,
        telefone: item.telefone,
        endereco: item.endereco,
        tipo: item.tipo,
        id_nacionalidade: item.id_nacionalidade,
        nacionalidade: item.nacionalidade, // se API devolver campo
        ...item
      })));
    } catch (error) {
      sms_error('Erro ao buscar fornecedores activos: ' + (error.message || 'Erro desconhecido'));
    } finally {
      setLoading(false);
    }
  };

  const fetchFornecedoresInativos = async () => {
    try {
      const response = await Api.ListFornecedorInativo(); // espera os inativos
      const dados = Array.isArray(response) ? response : [];
      setFornecedoresInativos(dados.map(item => ({
        key: item.id,
        nome: item.nome,
        nif: item.nif,
        telefone: item.telefone,
        endereco: item.endereco,
        tipo: item.tipo,
        id_nacionalidade: item.id_nacionalidade,
        nacionalidade: item.nacionalidade,
        ...item
      })));
    } catch (error) {
      sms_error('Erro ao buscar fornecedores inativos: ' + (error.message || 'Erro desconhecido'));
    }
  };

  const fetchNacionalidades = async () => {
    try {
      const response = await Api.ListNacionalidade();
      setNacionalidades(Array.isArray(response) ? response : []);
    } catch (error) {
      sms_error('Erro ao buscar nacionalidades: ' + (error.message || 'Erro desconhecido'));
    }
  };

  const handleAdd = async (values) => {
    setAddLoading(true);
    try {
      const payload = {
        ...values
      };
      const result = await Api.AddFornecedor(payload);
      if (result && result.success) {
        sms_success('Fornecedor adicionado com sucesso!');
        setIsAddModalVisible(false);
        form.resetFields();
        await fetchFornecedoresAtivos();
      } else {
        sms_error(result?.message || 'Erro ao adicionar fornecedor');
      }
    } catch (error) {
      sms_error('Erro ao adicionar fornecedor: ' + (error.message || 'Erro desconhecido'));
    } finally {
      setAddLoading(false);
    }
  };

  const handleEdit = async (values) => {
    setEditLoading(true);
    try {
      const payload = { id: selectedFornecedor.key, ...values };
      const result = await Api.AlterFornecedor(payload);
      if (result && result.success) {
        sms_success('Fornecedor editado com sucesso!');
        setIsEditModalVisible(false);
        editForm.resetFields();
        await fetchFornecedoresAtivos();
      }
      else if (result.message==='Request failed with status code 409')
        {
          sms_error('Não pode editar este fornecedor');
       }
      else {
        sms_error(result?.message || 'Erro ao editar fornecedor');
      }
    } catch (error) {
      sms_error('Erro ao editar fornecedor: ' + (error.message || 'Erro desconhecido'));
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      const result = await Api.DeleteFornecedor({ id: fornecedorToDelete });
      if (result && result.success) {
        sms_success('Fornecedor excluído com sucesso!');
        await fetchFornecedoresAtivos();
      } 
       else if (result.message==='Request failed with status code 501')
        {
          sms_error('Não pode excluir este fornecedor');
       }
      else {
        sms_error(result?.message || 'Erro ao excluir fornecedor');
      }
    } catch (error) {
      sms_error('Erro ao excluir fornecedor: ' + (error.message || 'Erro desconhecido'));
    } finally {
      setDeleteLoading(false);
      setIsDeleteModalVisible(false);
    }
  };

  const handleRestore = async (id) => {
    setRestoreLoading(true);
    try {
      const result = await Api.AlterStatusFornecedor({ id, status: 1 });
      if (result && result.success) {
        sms_success('Fornecedor restaurado com sucesso!');
        await fetchFornecedoresAtivos();
        await fetchFornecedoresInativos();
      } else {
        sms_error(result?.message || 'Erro ao restaurar fornecedor');
      }
    } catch (error) {
      sms_error('Erro ao restaurar fornecedor: ' + (error.message || 'Erro desconhecido'));
    } finally {
      setRestoreLoading(false);
    }
  };

  const columnsAtivos = [
    { title: 'Nome', dataIndex: 'nome', key: 'nome' },
    { title: 'NIF', dataIndex: 'nif', key: 'nif' },
    { title: 'Telefone', dataIndex: 'telefone', key: 'telefone' },
    { title: 'Endereço', dataIndex: 'endereco', key: 'endereco' },
    { title: 'Tipo', dataIndex: 'tipo', key: 'tipo' },
    { title: 'Nacionalidade', dataIndex: 'nacionalidade', key: 'nacionalidade' },
    {
      title: 'Ações',
      key: 'acoes',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 10 }}>
          <FaEdit
            style={{ cursor: 'pointer', color: '#1890ff' }}
            onClick={() => {
              setSelectedFornecedor(record);
              // ajusta valores para o form de edição (garante id_nacionalidade e tipo)
              editForm.setFieldsValue({
                nome: record.nome,
                nif: record.nif,
                telefone: record.telefone,
                endereco: record.endereco,
                tipo: record.tipo || 'SINGULAR',
                id_nacionalidade: record.id_nacionalidade ?? 3
              });
              setIsEditModalVisible(true);
            }}
          />
          <FaRegTrashAlt
            style={{ cursor: 'pointer', color: '#ff4d4f' }}
            onClick={() => {
              setFornecedorToDelete(record.key);
              setIsDeleteModalVisible(true);
            }}
          />
        </div>
      )
    }
  ];

  const columnsInativos = [
    { title: 'Nome', dataIndex: 'nome', key: 'nome' },
    { title: 'NIF', dataIndex: 'nif', key: 'nif' },
    { title: 'Telefone', dataIndex: 'telefone', key: 'telefone' },
    { title: 'Endereço', dataIndex: 'endereco', key: 'endereco' },
    { title: 'Tipo', dataIndex: 'tipo', key: 'tipo' },
    { title: 'Nacionalidade', dataIndex: 'nacionalidade', key: 'nacionalidade' },
    {
      title: 'Ações',
      key: 'acoes',
      render: (_, record) => (
        <Button
          type="link"
          icon={<FaUndo style={{ color: 'green' }} />}
          onClick={() => handleRestore(record.key)}
          loading={restoreLoading}
        >
          Restaurar
        </Button>
      )
    }
  ];

  return (
    <div>
      <ToastContainer />
      <Tabs defaultActiveKey="1">
        <TabPane tab="Fornecedores Activos" key="1">
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                // valores default ao abrir modal adicionar
                form.setFieldsValue({ id_nacionalidade: 3, tipo: 'SINGULAR' });
                setIsAddModalVisible(true);
              }}
            >
              Adicionar
            </Button>
          </div>

          <Spin spinning={loading}>
            <Table columns={columnsAtivos} dataSource={fornecedoresAtivos} rowKey="key" />
          </Spin>
        </TabPane>

        <TabPane tab="Fornecedores Inactivos" key="2">
          <Spin spinning={loading}>
            <Table columns={columnsInativos} dataSource={fornecedoresInativos} rowKey="key" />
          </Spin>
        </TabPane>
      </Tabs>

      {/* Modal Adicionar */}
      <Modal
        title="Adicionar Fornecedor"
        visible={isAddModalVisible}
        onCancel={() => {
          form.resetFields();
          setIsAddModalVisible(false);
        }}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleAdd}>
          {/* Nome + NIF */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="nome"
                label="Nome"
                rules={[{ required: true, message: 'Nome é obrigatório' }]}
              >
                <Input maxLength={150} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="nif"
                label="NIF"
                rules={[{ required: true, message: 'NIF é obrigatório' }]}
              >
                <Input maxLength={20} />
              </Form.Item>
            </Col>
          </Row>

          {/* Telefone + Tipo + Nacionalidade */}
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="telefone"
                label="Telefone"
                rules={[{ required: true, message: 'Telefone é obrigatório' }]}
              >
                <Input maxLength={20} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="tipo"
                label="Tipo"
                rules={[{ required: true, message: 'Tipo é obrigatório' }]}
              >
                <Select>
                  <Option value="SINGULAR">SINGULAR</Option>
                  <Option value="COLECTIVA">COLECTIVA</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="id_nacionalidade"
                label="Nacionalidade"
                rules={[{ required: true, message: 'Nacionalidade é obrigatória' }]}
              >
                <Select>
                  {nacionalidades.map(nac => (
                    <Option key={nac.id} value={nac.id}>
                      {nac.nacionalidade}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Endereço */}
          <Form.Item
            name="endereco"
            label="Endereço"
            rules={[{ required: true, message: 'Endereço é obrigatório' }]}
          >
            <Input.TextArea rows={2} maxLength={250} />
          </Form.Item>

          <Form.Item>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                style={{ marginRight: 8 }}
                onClick={() => {
                  form.resetFields();
                  setIsAddModalVisible(false);
                }}
                disabled={addLoading}
              >
                Cancelar
              </Button>
              <Button type="primary" htmlType="submit" loading={addLoading}>
                {addLoading ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Editar */}
      <Modal
        title="Editar Fornecedor"
        visible={isEditModalVisible}
        onCancel={() => {
          editForm.resetFields();
          setIsEditModalVisible(false);
        }}
        footer={null}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" onFinish={handleEdit}>
          {/* Nome + NIF */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="nome" label="Nome" rules={[{ required: true }]}>
                <Input maxLength={150} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="nif" label="NIF" rules={[{ required: true }]}>
                <Input maxLength={20} />
              </Form.Item>
            </Col>
          </Row>

          {/* Telefone + Tipo + Nacionalidade */}
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="telefone" label="Telefone" rules={[{ required: true }]}>
                <Input maxLength={20} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="tipo" label="Tipo" rules={[{ required: true }]}>
                <Select>
                  <Option value="SINGULAR">SINGULAR</Option>
                  <Option value="COLECTIVA">COLECTIVA</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="id_nacionalidade" label="Nacionalidade" rules={[{ required: true }]}>
                <Select>
                  {nacionalidades.map(nac => (
                    <Option key={nac.id} value={nac.id}>
                      {nac.nacionalidade}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Endereço */}
          <Form.Item name="endereco" label="Endereço" rules={[{ required: true }]}>
            <Input.TextArea rows={2} maxLength={250} />
          </Form.Item>

          <Form.Item>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                style={{ marginRight: 8 }}
                onClick={() => {
                  editForm.resetFields();
                  setIsEditModalVisible(false);
                }}
                disabled={editLoading}
              >
                Cancelar
              </Button>
              <Button type="primary" htmlType="submit" loading={editLoading}>
                {editLoading ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Excluir */}
      <Modal
        title="Confirmar Exclusão"
        visible={isDeleteModalVisible}
        onOk={handleDelete}
        onCancel={() => setIsDeleteModalVisible(false)}
        okText="Sim"
        cancelText="Não"
        confirmLoading={deleteLoading}
      >
        <p>Tem certeza que deseja excluir este fornecedor?</p>
      </Modal>
    </div>
  );
};

export default Fornecedor;
